import { countLines } from './code';
import { codeAt, firstTokenAt, runEnd, type Run } from './run';

const s2 = (ms: number) => (ms / 1000).toFixed(2);

const STEP_MS = 100;

function phaseAt(run: Run, t: number): string {
  const ttft = firstTokenAt(run);
  if (ttft === null || t < ttft) return 'waiting for first token';
  if (run.streamEndedAt === null || t < run.streamEndedAt) return 'streaming';
  if (run.phase === 'incomplete') return 'stopped · max_tokens';
  if (run.phase === 'stream-failed') return 'stream failed';
  if (run.settledAt === null || t < run.settledAt) return 'compiling';
  return run.phase === 'rendered' ? 'rendered' : 'compile failed';
}

interface ScrubberProps {
  run: Run;
  at: number | null;
  onScrub: (t: number | null) => void;
}

/** Rewinds a finished run along its own recorded timeline; code and preview follow the position. */
export function Scrubber({ run, at, onScrub }: ScrubberProps) {
  const end = runEnd(run);
  if (end === null) throw new Error('The scrubber needs a run that has ended.');
  const t = at ?? end;
  const ttft = firstTokenAt(run);
  const marks = [
    ttft === null ? null : ([ttft, 'first token'] as const),
    run.streamEndedAt === null ? null : ([run.streamEndedAt, 'stream end'] as const),
    run.settledAt !== null && (run.phase === 'rendered' || run.phase === 'render-failed')
      ? ([run.settledAt, run.phase === 'rendered' ? 'rendered' : 'compile failed'] as const)
      : null,
  ].filter((m) => m !== null);
  const phase = phaseAt(run, t);
  const lines = countLines(codeAt(run, t));
  const width = s2(end).length;
  const top = Math.ceil(end / STEP_MS) * STEP_MS;
  const pct = top === 0 ? 100 : (Math.min(t, top) / top) * 100;

  return (
    <div className="scrub flex h-[34px] shrink-0 items-center gap-3.5 border-t border-white/10 bg-[#0c0c11] pr-3 pl-5 font-mono text-[11px] text-white/50 max-sm:gap-2.5 max-sm:pr-2 max-sm:pl-4">
      <span className="tracking-[0.12em] text-white/40 uppercase max-sm:hidden">Rewind</span>
      <span className="whitespace-nowrap text-white/45 tabular-nums">
        <b className="inline-block text-right font-medium text-white/90" style={{ minWidth: `${width}ch` }}>
          {s2(t)}
        </b>{' '}
        s / {s2(end)} s
      </span>
      <div className="relative flex h-5 min-w-[60px] flex-1 items-center">
        <input
          type="range"
          min={0}
          max={top}
          step={STEP_MS}
          value={at === null ? top : Math.min(Math.round(at / STEP_MS) * STEP_MS, top)}
          aria-label="Rewind the build: code and preview move together"
          aria-valuetext={`${s2(t)} seconds, ${phase}, ${lines} lines`}
          style={{ ['--p' as string]: `${pct}%` }}
          onChange={(e) => {
            const v = Number(e.currentTarget.value);
            onScrub(v >= end ? null : v);
          }}
        />
        {marks.map(([mark, label]) => (
          <span
            key={label}
            title={label}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 -mt-[4.5px] -ml-px h-[9px] w-px bg-white/30"
            style={{ left: `calc(6px + (100% - 12px) * ${top === 0 ? 1 : mark / top})` }}
          />
        ))}
      </div>
      <span className="whitespace-nowrap text-white/55 max-sm:hidden">
        {phase} · {lines} lines
      </span>
      <button
        type="button"
        disabled={at === null}
        onClick={() => onScrub(null)}
        className="shrink-0 rounded-md border border-white/14 px-2 py-0.5 text-[10.5px] font-medium tracking-[0.1em] text-white/75 uppercase transition-colors enabled:hover:border-white/30 enabled:hover:text-white disabled:cursor-default disabled:opacity-35"
      >
        Latest
      </button>
    </div>
  );
}
